import I from 'immutable';
import moment from 'moment';
import { t } from 'ttag';

function isMoment(date) {
    return moment.isMoment(date);
}

/**
 * @param {Date|string|null} date
 * @param {string} [format]
 * @return {moment.Moment|null}
 */
function toMoment(date, format = '') {
    date = isMoment(date ?? null) ? date : moment(date ?? null, format);
    return date.isValid() ? date : null;
}

function dateNow() {
    return moment().toDate();
}

function addDate(date, addValue, unit = 'day') {
    const validDate = toMoment(date);
    return validDate ? validDate.clone().add(addValue, unit).toDate() : null;
}

/**
 * @param {Date} date
 * @param {number} amount
 * @param {'day'|'month'|'year'} [unit='day']
 * @return {Date|null}
 */
function subtractDate(date, amount, unit = 'day') {
    const validDate = toMoment(date);
    return validDate ? validDate.clone().subtract(amount, unit).toDate() : null;
}

/**
 * @param {Date|null} [dateToCheck=null]
 * @param {Date|null} [targetDate=null]
 * @param {'day'|'month'|'year'} [unit='day']
 * @return {boolean}
 */
function isDateBefore(dateToCheck = null, targetDate = null, unit = 'day') {
    return !dateToCheck || !targetDate
        ? false
        : moment(dateToCheck).isBefore(targetDate, unit);
}

function getQuarterInfo(date) {
    date = toMoment(date);
    if (!date) return null;

    return {
        index: date.quarter(),
        dateStart: date.startOf('quarter').toDate(),
        dateEnd: date.endOf('quarter').toDate(),
    };
}

function getLastQuarters(date, count = 0) {
    date = toMoment(date);
    if (!date) return null;
    return new I.Range(0, count)
        .map((idx) => {
            const currentDate = addDate(date, -idx, 'quarter');
            const { index, dateStart, dateEnd } = getQuarterInfo(currentDate);
            const year = toMoment(currentDate).year();
            return {
                dateStart,
                dateEnd,
                value: `last_quarter_${idx + 1}`,
                label: t`${index}й квартал ${year}`,
            };
        })
        .toArray();
}

function formatDate(date, format = 'DD.MM.YY') {
    const validDate = toMoment(date);
    if (validDate) return validDate.format(format);
    return null;
}

/**
 * @param {MomentInput} date1
 * @param {MomentInput} date2
 * @param {unitOfTime.StartOf} [unit]
 * @return {boolean}
 */
function isSame(date1, date2, unit = 'day') {
    if (!date1 || !date2) {
        return false;
    }

    const validDate1 = toMoment(date1);
    const validDate2 = toMoment(date2);

    return validDate1 && validDate2
        ? validDate1.isSame(validDate2, unit)
        : false;
}

function isToday(date) {
    if (!date) return false;
    return isSame(date, moment(), 'day');
}

function formatActualDate(date) {
    if (isToday(date)) {
        return formatDate(date, 'HH:mm');
    }
    return formatDate(date);
}

function toDate(dateString, format) {
    if (!dateString) return null;
    const validDate = toMoment(dateString, format);
    return validDate ? validDate.toDate() : null;
}

function toServerDate(date) {
    return date instanceof Date ? date.toISOString() : date;
}

function toStartOf(date, unit = 'day') {
    const validDate = toMoment(date);

    return validDate ? validDate.startOf(unit).toDate() : null;
}

function getPeriod(periodBegin, periodEnd, units = 'days') {
    const dateFrom = toMoment(periodBegin, 'DD.MM.YY');
    const dateTo = toMoment(periodEnd, 'DD.MM.YY');

    if (!dateFrom || !dateTo) {
        return null;
    }

    return dateTo.diff(dateFrom, units);
}

export {
    addDate,
    subtractDate,
    dateNow,
    getLastQuarters,
    getPeriod,
    getQuarterInfo,
    formatDate,
    formatActualDate,
    isMoment,
    isSame,
    isToday,
    toMoment,
    toDate,
    toServerDate,
    toStartOf,
    isDateBefore,
};
